import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Modal from "@mui/material/Modal";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { useAuth } from "../../context/AuthContext";
import api from "../../services/api";

const MATIERES = {
  quoran: { label: "Qour'an", bg: "#e8f5e9", color: "#2e7d32" },
  arabe: { label: "Langue arabe", bg: "#e3f2fd", color: "#1565c0" },
  science: { label: "Sciences de la religion", bg: "#fff3e0", color: "#e65100" },
};

// Retourne "Sourate" ou "Chapitre" selon la matière
const getTermeChapitre = (matiere, pluriel = false) => {
  if (matiere === "quoran") return pluriel ? "Sourates" : "Sourate";
  return pluriel ? "Chapitres" : "Chapitre";
};

function Livres() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [livres, setLivres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterMatiere, setFilterMatiere] = useState("");
  const isMobile = window.innerWidth < 768;

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedLivre, setSelectedLivre] = useState(null);
  const [chapitres, setChapitres] = useState([]);
  const [loadingChap, setLoadingChap] = useState(false);
  const [newChap, setNewChap] = useState({ titre: "", numero: "" });
  const [savingChap, setSavingChap] = useState(false);

  useEffect(() => { fetchLivres(); }, []);

  const fetchLivres = async () => {
    try {
      const { data } = await api.get("/livres");
      setLivres(data);
    } catch (error) {
      console.error("Erreur:", error);
    } finally {
      setLoading(false);
    }
  };

  const openChapitresModal = async (livre) => {
    setSelectedLivre(livre);
    setModalOpen(true);
    setLoadingChap(true);
    try {
      const { data } = await api.get(`/livres/${livre.id}/chapitres`);
      setChapitres(data);
    } catch (error) {
      console.error("Erreur:", error);
    } finally {
      setLoadingChap(false);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedLivre(null);
    setChapitres([]);
    setNewChap({ titre: "", numero: "" });
  };

  const addChapitre = async () => {
    if (!newChap.titre.trim() || !newChap.numero) return;
    setSavingChap(true);
    try {
      await api.post(`/livres/${selectedLivre.id}/chapitres`, newChap);
      const { data } = await api.get(`/livres/${selectedLivre.id}/chapitres`);
      setChapitres(data);
      setNewChap({ titre: "", numero: "" });
      fetchLivres();
    } catch (error) {
      alert(error.response?.data?.message || "Erreur");
    } finally {
      setSavingChap(false);
    }
  };

  const deleteChapitre = async (chapitreId) => {
    const terme = getTermeChapitre(selectedLivre?.matiere).toLowerCase();
    if (!window.confirm(`Supprimer ce${selectedLivre?.matiere === "quoran" ? "tte" : ""} ${terme} ?`)) return;
    try {
      await api.delete(`/livres/${selectedLivre.id}/chapitres/${chapitreId}`);
      const { data } = await api.get(`/livres/${selectedLivre.id}/chapitres`);
      setChapitres(data);
      fetchLivres();
    } catch (error) {
      alert(error.response?.data?.message || "Erreur");
    }
  };

  const deleteLivre = async (id) => {
    if (!window.confirm("Supprimer ce livre et tous ses chapitres/sourates ?")) return;
    try {
      await api.delete(`/livres/${id}`);
      fetchLivres();
    } catch (error) {
      alert(error.response?.data?.message || "Erreur");
    }
  };

  const filtered = livres.filter((l) => {
    const matchSearch = l.nom.toLowerCase().includes(search.toLowerCase());
    const matchMatiere = filterMatiere === "" || l.matiere === filterMatiere;
    return matchSearch && matchMatiere;
  });

  const modalMatiere = selectedLivre ? MATIERES[selectedLivre.matiere] : null;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card sx={{ borderRadius: "16px", boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>

              {/* Header */}
              <MDBox mx={2} mt={-3} py={3} px={3} sx={{
                background: "linear-gradient(135deg, #e65100 0%, #f57c00 100%)",
                borderRadius: "12px",
                boxShadow: "0 4px 20px rgba(230,81,0,0.4)",
                display: "flex", justifyContent: "space-between", alignItems: "center",
                flexWrap: "wrap", gap: 2,
              }}>
                <MDBox>
                  <MDTypography variant="h5" color="white" fontWeight="bold">{t("livres")}</MDTypography>
                  <MDTypography variant="caption" color="white" sx={{ opacity: 0.8 }}>
                    {livres.length} livre{livres.length > 1 ? "s" : ""} enregistré{livres.length > 1 ? "s" : ""}
                  </MDTypography>
                </MDBox>
                {isAdmin && (
                  <MDButton variant="contained" size="medium"
                    onClick={() => navigate("/livres/nouveau")}
                    sx={{
                      background: "#fff", color: "#e65100",
                      fontWeight: 700, borderRadius: "10px",
                      "&:hover": { background: "#fff3e0" },
                    }}>
                    <Icon sx={{ mr: { xs: 0, sm: 0.5 } }}>add</Icon>
                    <MDBox component="span" sx={{ display: { xs: "none", sm: "inline" } }}>{t("Ajouter livre")}</MDBox>
                  </MDButton>
                )}
              </MDBox>

              <MDBox pt={3} px={3} pb={3}>
                <MDBox display="flex" justifyContent="space-between"
                  alignItems="center" mb={3} flexWrap="wrap" gap={2}>
                  <MDBox sx={{ flex: 1, maxWidth: { xs: "100%", sm: 360 } }}>
                    <MDInput fullWidth
                      label="🔍  Rechercher un livre..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                    />
                  </MDBox>
                  <MDBox display="flex" gap={1} flexWrap="wrap">
                    <MDBox onClick={() => setFilterMatiere("")} sx={{
                      padding: "6px 14px", borderRadius: "20px", cursor: "pointer",
                      border: "1px solid", fontSize: { xs: 11, sm: 13 }, fontWeight: 500,
                      borderColor: filterMatiere === "" ? "#e65100" : "#d2d6da",
                      background: filterMatiere === "" ? "#e65100" : "#fff",
                      color: filterMatiere === "" ? "#fff" : "#344767",
                      transition: "all 0.2s",
                    }}>
                      Tous
                    </MDBox>
                    {Object.entries(MATIERES).map(([key, m]) => (
                      <MDBox key={key} onClick={() => setFilterMatiere(key)} sx={{
                        padding: "6px 14px", borderRadius: "20px", cursor: "pointer",
                        border: "1px solid", fontSize: { xs: 11, sm: 13 }, fontWeight: 500,
                        borderColor: filterMatiere === key ? m.color : "#d2d6da",
                        background: filterMatiere === key ? m.color : "#fff",
                        color: filterMatiere === key ? "#fff" : "#344767",
                        transition: "all 0.2s",
                      }}>
                        {isMobile ? m.label.split(" ")[0] : m.label}
                      </MDBox>
                    ))}
                  </MDBox>
                </MDBox>

                {loading ? (
                  <MDBox display="flex" justifyContent="center" py={4}>
                    <MDTypography variant="body2" color="text">{t("commun.chargement")}</MDTypography>
                  </MDBox>
                ) : filtered.length === 0 ? (
                  <MDBox display="flex" flexDirection="column" alignItems="center" py={6} gap={2}>
                    <Icon sx={{ fontSize: 60, color: "#e0e0e0" }}>library_books</Icon>
                    <MDTypography variant="h6" color="text" fontWeight="medium">
                      Aucun livre trouvé
                    </MDTypography>
                  </MDBox>
                ) : (
                  <Grid container spacing={2}>
                    {filtered.map((livre) => {
                      const m = MATIERES[livre.matiere] || { label: livre.matiere, bg: "#f5f5f5", color: "#333" };
                      const nbChapitres = livre.chapitres?.length || 0;
                      const terme = getTermeChapitre(livre.matiere, true).toLowerCase();
                      return (
                        <Grid item xs={6} sm={6} md={4} lg={3} key={livre.id}>
                          <MDBox sx={{
                            background: "#fff", borderRadius: "16px",
                            border: "1px solid #f0f0f0",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                            overflow: "hidden", transition: "all 0.2s",
                            "&:hover": { boxShadow: "0 8px 24px rgba(0,0,0,0.1)", transform: "translateY(-2px)" },
                          }}>
                            <MDBox sx={{
                              height: { xs: 130, sm: 200 }, background: m.bg,
                              display: "flex", alignItems: "center", justifyContent: "center",
                              position: "relative", overflow: "hidden",
                            }}>
                              {livre.couverture ? (
                                <img src={`${livre.couverture}`} alt={livre.nom}
                                  style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                              ) : (
                                <Icon sx={{ fontSize: { xs: 40, sm: 64 }, color: m.color, opacity: 0.25 }}>
                                  menu_book
                                </Icon>
                              )}
                              <MDBox sx={{
                                position: "absolute", top: 8, right: 8,
                                background: m.color, color: "#fff",
                                borderRadius: "10px", padding: "2px 8px",
                                fontSize: 10, fontWeight: 700,
                              }}>
                                {isMobile ? m.label.split(" ")[0] : m.label}
                              </MDBox>
                              <MDBox sx={{
                                position: "absolute", top: 8, left: 8,
                                background: "rgba(0,0,0,0.5)", color: "#fff",
                                borderRadius: "10px", padding: "2px 8px",
                                fontSize: 10, fontWeight: 600,
                              }}>
                                {nbChapitres} {terme.slice(0, 4)}.
                              </MDBox>
                            </MDBox>

                            <MDBox px={1.5} py={1.5}>
                              <MDTypography variant="button" fontWeight="bold"
                                sx={{ color: "#344767", display: "block", mb: 1, fontSize: { xs: 12, sm: 14 } }}>
                                {livre.nom}
                              </MDTypography>
                              <MDBox display="flex" gap={1}>
                                <MDBox onClick={() => openChapitresModal(livre)} sx={{
                                  flex: 1, padding: "6px", borderRadius: "8px",
                                  background: m.bg, color: m.color,
                                  display: "flex", alignItems: "center", justifyContent: "center",
                                  cursor: "pointer", fontSize: { xs: 11, sm: 12 }, fontWeight: 600, gap: 0.5,
                                  border: `1px solid ${m.color}22`,
                                  "&:hover": { background: m.color, color: "#fff" },
                                  transition: "all 0.2s",
                                }}>
                                  <Icon sx={{ fontSize: 14 }}>format_list_numbered</Icon>
                                  <MDBox component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                                    {getTermeChapitre(livre.matiere, true)}
                                  </MDBox>
                                </MDBox>
                                {isAdmin && (
                                  <>
                                    <MDBox onClick={() => navigate(`/livres/${livre.id}/modifier`)} sx={{
                                      width: 32, height: 32, borderRadius: "8px",
                                      background: "#fff8e1", color: "#f57f17",
                                      display: "flex", alignItems: "center", justifyContent: "center",
                                      cursor: "pointer", "&:hover": { background: "#ffecb3" },
                                    }}>
                                      <Icon sx={{ fontSize: 16 }}>edit</Icon>
                                    </MDBox>
                                    <MDBox onClick={() => deleteLivre(livre.id)} sx={{
                                      width: 32, height: 32, borderRadius: "8px",
                                      background: "#ffebee", color: "#c62828",
                                      display: "flex", alignItems: "center", justifyContent: "center",
                                      cursor: "pointer", "&:hover": { background: "#ffcdd2" },
                                    }}>
                                      <Icon sx={{ fontSize: 16 }}>cancel</Icon>
                                    </MDBox>
                                  </>
                                )}
                              </MDBox>
                            </MDBox>
                          </MDBox>
                        </Grid>
                      );
                    })}
                  </Grid>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Modale chapitres/sourates */}
      <Modal open={modalOpen} onClose={closeModal}>
        <MDBox sx={{
          position: "absolute", top: "50%", left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "95%", sm: 560 },
          maxHeight: "85vh",
          background: "#fff", borderRadius: "20px",
          boxShadow: "0 24px 60px rgba(0,0,0,0.2)",
          outline: "none", overflow: "hidden",
          display: "flex", flexDirection: "column",
        }}>
          {selectedLivre && (
            <>
              <MDBox px={3} py={2.5} sx={{
                background: `linear-gradient(135deg, ${modalMatiere?.color} 0%, ${modalMatiere?.color}cc 100%)`,
                display: "flex", justifyContent: "space-between", alignItems: "center",
              }}>
                <MDBox>
                  <MDTypography variant="h6" color="white" fontWeight="bold">
                    {selectedLivre.nom}
                  </MDTypography>
                  <MDTypography variant="caption" color="white" sx={{ opacity: 0.85 }}>
                    {modalMatiere?.label} · {chapitres.length} {getTermeChapitre(selectedLivre.matiere, chapitres.length > 1).toLowerCase()}
                  </MDTypography>
                </MDBox>
                <MDBox onClick={closeModal} sx={{
                  width: 32, height: 32, borderRadius: "50%",
                  background: "rgba(255,255,255,0.2)", cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  "&:hover": { background: "rgba(255,255,255,0.35)" },
                }}>
                  <Icon sx={{ color: "#fff", fontSize: 18 }}>close</Icon>
                </MDBox>
              </MDBox>

              <MDBox sx={{ flex: 1, overflowY: "auto", p: 3 }}>
                {isAdmin && (
                  <MDBox mb={3} p={2} sx={{
                    background: modalMatiere?.bg, borderRadius: "12px",
                    border: `1px solid ${modalMatiere?.color}33`,
                  }}>
                    <MDTypography variant="caption" fontWeight="bold" sx={{
                      color: modalMatiere?.color, textTransform: "uppercase",
                      fontSize: 11, letterSpacing: "0.05em", display: "block", mb: 1.5,
                    }}>
                      Ajouter {selectedLivre.matiere === "quoran" ? "une sourate" : "un chapitre"}
                    </MDTypography>
                    <MDBox display="flex" gap={1.5} alignItems="flex-end" flexWrap="wrap">
                      <MDBox sx={{ width: { xs: "100%", sm: 80 } }}>
                        <MDTypography variant="caption" color="text" sx={{ display: "block", mb: 0.5, fontSize: 11 }}>
                          Numéro
                        </MDTypography>
                        <input type="number" value={newChap.numero}
                          onChange={(e) => setNewChap((p) => ({ ...p, numero: e.target.value }))}
                          placeholder="N°" min="1"
                          style={{
                            width: "100%", height: 40, padding: "0 10px",
                            border: "1px solid #d2d6da", borderRadius: "8px",
                            fontSize: 14, outline: "none", boxSizing: "border-box",
                          }}
                        />
                      </MDBox>
                      <MDBox sx={{ flex: 1, minWidth: 150 }}>
                        <MDTypography variant="caption" color="text" sx={{ display: "block", mb: 0.5, fontSize: 11 }}>
                          {selectedLivre.matiere === "quoran" ? "Nom de la sourate" : "Titre du chapitre"}
                        </MDTypography>
                        <input type="text" value={newChap.titre}
                          onChange={(e) => setNewChap((p) => ({ ...p, titre: e.target.value }))}
                          placeholder={selectedLivre.matiere === "quoran" ? "Ex: Al-Fatiha..." : "Ex: Introduction..."}
                          onKeyDown={(e) => e.key === "Enter" && addChapitre()}
                          style={{
                            width: "100%", height: 40, padding: "0 12px",
                            border: "1px solid #d2d6da", borderRadius: "8px",
                            fontSize: 14, outline: "none", boxSizing: "border-box",
                          }}
                        />
                      </MDBox>
                      <MDBox onClick={addChapitre} sx={{
                        height: 40, px: 2, borderRadius: "8px",
                        background: savingChap ? "#ccc" : modalMatiere?.color,
                        color: "#fff", display: "flex", alignItems: "center",
                        justifyContent: "center", cursor: savingChap ? "default" : "pointer",
                        fontSize: 13, fontWeight: 700, gap: 0.5, whiteSpace: "nowrap",
                        transition: "all 0.2s", flexShrink: 0,
                      }}>
                        <Icon sx={{ fontSize: 16 }}>add</Icon>
                        {savingChap ? "..." : "Ajouter"}
                      </MDBox>
                    </MDBox>
                  </MDBox>
                )}

                {loadingChap ? (
                  <MDBox display="flex" justifyContent="center" py={3}>
                    <MDTypography variant="body2" color="text">{t("commun.chargement")}</MDTypography>
                  </MDBox>
                ) : chapitres.length === 0 ? (
                  <MDBox display="flex" flexDirection="column" alignItems="center" py={4} gap={1}>
                    <Icon sx={{ fontSize: 48, color: "#e0e0e0" }}>format_list_numbered</Icon>
                    <MDTypography variant="body2" color="text">
                      {selectedLivre.matiere === "quoran"
                        ? "Aucune sourate pour l'instant"
                        : "Aucun chapitre pour l'instant"}
                    </MDTypography>
                  </MDBox>
                ) : (
                  <MDBox>
                    <MDTypography variant="caption" fontWeight="bold" sx={{
                      textTransform: "uppercase", fontSize: 11,
                      letterSpacing: "0.05em", color: "#9e9e9e", display: "block", mb: 1.5,
                    }}>
                      {chapitres.length} {getTermeChapitre(selectedLivre.matiere, chapitres.length > 1).toLowerCase()}
                    </MDTypography>
                    {chapitres.map((chap, index) => (
                      <MDBox key={chap.id}
                        display="flex" alignItems="center" justifyContent="space-between"
                        py={1.2} px={2} mb={0.8}
                        sx={{
                          background: index % 2 === 0 ? "#f8f8f8" : "#fff",
                          borderRadius: "10px", border: "1px solid #f0f0f0",
                          transition: "all 0.15s",
                          "&:hover": { background: modalMatiere?.bg, borderColor: `${modalMatiere?.color}33` },
                        }}>
                        <MDBox display="flex" alignItems="center" gap={2}>
                          <MDBox sx={{
                            width: 28, height: 28, borderRadius: "50%",
                            background: modalMatiere?.color, color: "#fff",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 12, fontWeight: 700, flexShrink: 0,
                          }}>
                            {chap.numero}
                          </MDBox>
                          <MDTypography variant="body2" sx={{ color: "#344767", fontWeight: 500 }}>
                            {chap.titre}
                          </MDTypography>
                        </MDBox>
                        {isAdmin && (
                          <MDBox onClick={() => deleteChapitre(chap.id)} sx={{
                            width: 28, height: 28, borderRadius: "8px",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            cursor: "pointer", color: "#e0e0e0",
                            "&:hover": { background: "#ffebee", color: "#c62828" },
                          }}>
                            <Icon sx={{ fontSize: 16 }}>cancel</Icon>
                          </MDBox>
                        )}
                      </MDBox>
                    ))}
                  </MDBox>
                )}
              </MDBox>

              <MDBox px={3} py={2} sx={{ borderTop: "1px solid #f0f0f0" }}>
                <MDButton variant="outlined" color="secondary" size="small"
                  onClick={closeModal} fullWidth>
                  Fermer
                </MDButton>
              </MDBox>
            </>
          )}
        </MDBox>
      </Modal>

      <Footer />
    </DashboardLayout>
  );
}

export default Livres;